import {createContext, useEffect, useMemo, useState} from "react";


export const THEME_KEY = "user-theme";
export const root = () => document.documentElement;
export const APP_ALLOWED_THEMES = ["light", "dark", "system"] as const;
export type THEME_TYPE = (typeof APP_ALLOWED_THEMES)[number];
export const getSystemTheme = () =>
    window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
export const applyTheme = (theme: string) => {
    let mode: "light" | "dark";
    if (theme === "system") {
        mode = getSystemTheme()
    } else {
        mode = theme as "light" | "dark";
    }
    root().setAttribute("data-theme", mode)
    return mode
}

type ThemeProviderProps = {
    theme: THEME_TYPE,
    mode: "light" | "dark",
    toggleTheme: () => void,
    setTheme: (newTheme: THEME_TYPE) => void,
}

export const ThemeContext = createContext<ThemeProviderProps | null>(null)
export const ThemeProvider = ({children}:{children: any}) => {
    const [theme, setTheme] = useState<THEME_TYPE>((localStorage.getItem(THEME_KEY) as THEME_TYPE) || "light");
    const mode = useMemo(() => applyTheme(theme), [theme]);
    useEffect(() => {
        localStorage.setItem(THEME_KEY, theme);
    }, [theme]);
    useEffect(() => {
        if (theme !== "system") return;
        const mql = window.matchMedia("(prefers-color-scheme: dark)");
        const handler = () => setTheme("system");

        mql.addEventListener("change", handler);
        return () => mql.removeEventListener("change", handler);
    }, [theme]);

    const toggleTheme = () => {
        setTheme(mode === "dark" ? "light" : "dark")
    };
    const setThemeFunc = (theme: THEME_TYPE) => {
        setTheme(theme);
    }
    const value = useMemo(
        () => ({mode, toggleTheme, theme, setTheme: setThemeFunc}),
        [mode, theme]
    );
    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    )
}